import {Children, forwardRef, isValidElement, useCallback, useRef} from 'react';
import type {Key, RefObject, ForwardedRef, HTMLAttributes} from 'react';
import {useMenuItem} from '@react-aria/menu';
import classNames from 'classnames';
import isString from '@tinkoff/utils/is/string';
import {mergeProps, mergeRefs} from '@react-aria/utils';
import type {TreeState} from '@react-stately/tree';
import type {FocusableElement, Node} from '@react-types/shared';

import {ChevronDownIcon} from '../../../icons/chevron-down';
import {MenuItemHotkey, MenuItemText} from '../../../atoms/menu-item/src';
import {HotkeyListener} from '../../../molecules/hotkey-listener';

import styles from './menu-item.module.css';

type Props<Item extends object> = HTMLAttributes<HTMLLIElement> & {
    item: Node<Item>;
    state: TreeState<Item>;
    onAction?: (key: Key) => void;
    onClose?: () => void;
    hasSubMenu?: boolean;
};

export const MenuItem = forwardRef(
    <Item extends object>(
        {item, state, onAction, onClose, hasSubMenu, ...props}: Props<Item>,
        passedRef: ForwardedRef<FocusableElement>,
    ) => {
        const hotkey = findHotkey(item);
        const ownRef = useRef<FocusableElement>(null);
        const {menuItemProps} = useMenuItem(
            {
                key: item.key,
                onAction,
                onClose,
            },
            state,
            ownRef as RefObject<FocusableElement>,
        );

        const isFocused = state.selectionManager.focusedKey === item.key;

        const hotkeyCallback = useCallback(() => {
            onAction?.(item.key);
            onClose?.();
        }, [item.key, onAction, onClose]);

        const renderItem = (ref: RefObject<HTMLLIElement>) => (
            <li
                {...mergeProps(menuItemProps, props)}
                className={classNames(styles.item, {
                    [styles.focused]: isFocused,
                })}
                ref={ref as RefObject<HTMLLIElement>}
            >
                {isString(item.rendered) ? <MenuItemText primary={item.rendered} /> : item.rendered}
                {hasSubMenu && (
                    <span className={styles.moreIcon}>
                        <ChevronDownIcon className={styles.chevron} width={20} height={20} />
                    </span>
                )}
            </li>
        );

        // eslint-disable-next-line no-negated-condition
        return hotkey !== undefined ? (
            <HotkeyListener keys={hotkey} callback={hotkeyCallback}>
                {(hotkeyTarget) => renderItem(mergeRefs(passedRef, ownRef, hotkeyTarget) as RefObject<HTMLLIElement>)}
            </HotkeyListener>
        ) : (
            renderItem(mergeRefs(passedRef, ownRef) as RefObject<HTMLLIElement>)
        );
    },
);

function findHotkey(item: Node<object>) {
    let hotkey: string | undefined;

    Children.forEach(item.rendered, (el) => {
        if (isValidElement(el) && el.type === MenuItemHotkey) {
            hotkey = el.props.hotkey;
        }
    });

    return hotkey;
}
